<template>
	<div class="sideways-content-fold" v-if="activePanel">
		<div class="portlet light bordered">
			<div class="portlet-title">
				<div class="caption">
					Assign App Permissions For <b>{{ editingRole.role_name }}</b>
				</div>
			</div>
			<div class="portlet-body">
				<form role="form" @submit.prevent="modifyRole($event)" novalidate>
					<div class="margin-top-20 row search-content-4">
						<div class="search-table table-responsive">
							<table class="table table-striped table-condensed">
								<thead>
									<tr>
										<th></th>
										<th><a class="no-click font-grey-mint">No Access</a></th>
										<th><a class="no-click font-grey-mint">View</a></th>
										<th><a class="no-click font-grey-mint">View &amp; Modify</a></th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="app in apps">
										<td><h4>{{ app.app_name }}</h4></td>
										<td>
											<div class="md-radio">
												<input type="radio" :id="`app_privileges_no_access_${app.app_id}`" :name="`app_privileges_${app.app_id}`" class="md-radiobtn" v-model="app.access_level" value="none">
												<label :for="`app_privileges_no_access_${app.app_id}`">
													<span></span>
													<span class="check"></span>
													<span class="box"></span>
												</label>
											</div>
										</td>
										<td>
											<div class="md-radio">
												<input type="radio" :id="`app_privileges_view_${app.app_id}`" :name="`app_privileges_${app.app_id}`" class="md-radiobtn" v-model="app.access_level" value="view">
												<label :for="`app_privileges_view_${app.app_id}`">
													<span></span>
													<span class="check"></span>
													<span class="box"></span>
												</label>
											</div>
										</td>
										<td>
											<div class="md-radio">
												<input type="radio" :id="`app_privileges_modify_${app.app_id}`" :name="`app_privileges_${app.app_id}`" class="md-radiobtn" v-model="app.access_level" value="modify">
												<label :for="`app_privileges_modify_${app.app_id}`">
													<span></span>
													<span class="check"></span>
													<span class="box"></span>
												</label>
											</div>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</div>
					<button type="button" class="btn btn-default exit-invitation-setup" @click="closePanel()"> Return To Roles Dashboard</button>
					<button type="submit" class="btn blue" :disabled="editingRole.locked_role">Submit</button>
				</form>
			</div>
		</div>
	</div>
</template>
<script>
import GlobalFunctions from '../../../../global'
import Role from '../../../../controllers/Roles'
import Business from '../../../../controllers/Business'
import { find } from 'lodash'

/**
 * Edit Role Permissions module is a page for editing the permissions of a role.
 * @class EditRolePermissions
 * @extends Roles
 * @prop {number} businessId - Used to pass the business id of the business the role belongs to.
 * @prop {string} baseRoute - The route of the component that this is being called from.
 * @prop {string} baseRouteName - The name of the route of the component that this is being called from.
 * @since 0.0.6
 */

export default {
	name: 'edit-role-permissions',
	props: {
		businessId: {
			type: Number,
			required: true
		},
		baseRoute: {
			type: String,
			required: true
		},
		baseRouteName: {
			type: String,
			required: true
		}
	},
	data () {
		return {
			activePanel: false,
			errorMessage: '',
			apps: [],
			editingRole: {
				app_permission: []
			},
			roleId: 0
		}
	},
	/**
	 * Run on created to determine if this should be the active panel or not.
	 * @function
	 * @return {undefined}
	 * @memberof EditRolePermissions
	 * @version 0.0.8
	 */
	created () {
		if (this.$route.name === this.baseRouteName + ' - Role Permissions') {
			this.activePanel = true
			if (this.$route.params.role_id && this.$route.params.role_id !== 'undefined' && parseInt(this.$route.params.role_id) !== 0) {
				this.roleId = this.$route.params.role_id
				this.resetApps()
				this.getBusinessDetails()
			} else {
				this.closePanel()
			}
		} else {
			this.activePanel = false
		}
	},
	methods: {
		/**
		 * To clear the current error.
		 * @function
		 * @returns {undefined}
		 * @memberof EditRolePermissions
		 * @version 0.0.6
		 */
		clearError () {
			this.errorMessage = ''
		},
		/**
		 * To set all app access levels to 'none'
		 * @function
		 * @returns {undefined}
		 * @memberof EditRolePermissions
		 * @version 0.0.6
		 */
		resetApps () {
			this.apps.forEach((app, index, apps) => {
				apps[index].access_level = 'none'
			})
		},
		/**
		 * To pull in details about the current business, mainly for getting list of available apps.
		 * @function
		 * @return {object} A promise that will either return an error message or display the success screen.
		 * @memberof EditRolePermissions
		 * @version 0.0.8
		 */
		getBusinessDetails () {
			let _this = this

			return Business.getBusinessDetails(this.businessId).then(response => {
				_this.apps = response.payload.apps

				this.apps.forEach((app, index, apps) => {
					apps[index].access_level = 'none'
				})
				this.getRole()
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'business_not_found':
						// Do something
						break

					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To get details for the current role.
		 * @function
		 * @return {undefined}
		 * @memberof EditRolePermissions
		 * @version 0.0.6
		 */
		getRole () {
			let _this = this

			Role.getRole(_this.roleId).then(response => {
				_this.editingRole = response.payload

				// Mapping role access levels to the list of apps
				_this.apps.forEach((app, index, apps) => {
					let localApp = find(_this.editingRole.app_permissions, ['app_id', apps[index].app_id])
					if (localApp) {
						apps[index].access_level = localApp.access_level
					}
				})
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'role_not_found':
						_this.closePanel()
						break

					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To send the updated details of the role to the backend.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @param {boolean} bypassConflict - Whether or not we should bypass any conflicts.
		 * @returns {object} A promise that will either return an error message or display the success screen.
		 * @memberof EditRolePermissions
		 * @version 0.0.6
		 */
		modifyRole (event, bypassConflict) {
			let disabledButton = GlobalFunctions.disableButton(event)
			let _this = this

			this.clearError()
			Role.modifyRole(_this.editingRole, bypassConflict).then(response => {
				_this.closePanel()
				disabledButton.complete()
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'already_exists':
						// This case should never happen.
						_this.closePanel()
						break

					case 'role_conflict':
						_this.$swal({
							title: 'Are you sure?',
							html: 'There is already a role that has these permissions. Do you still want to set these permissions?',
							type: 'warning',
							showCancelButton: true,
							confirmButtonText: 'Continue',
							confirmButtonColor: '#d9534f',
							cancelButtonText: 'Cancel',
							allowOutsideClick: false,
							allowEscapeKey: false,
							allowEnterKey: false,
							showLoaderOnConfirm: true
						}).then(() => {
							_this.modifyRole(false, true)
						}, dismiss => {
							// Do nothing on dismiss
						})
						break

					case 'role_not_found':
						_this.errorMessage = 'The role you are trying to edit could not be found. Please try selecting another role.'
						break

					case 'locked_role':
						_this.errorMessage = 'The role you are trying to edit is a locked role. You cannot perform this action.'
						break

					case 'invalid_role':
						_this.errorMessage = 'You don\t have access to modify this role. Please try selecting another role.'
						break

					default:
						// No default
					}
				}
				disabledButton.cancel()
			}))
		},
		/**
		 * To clear the object 'editingRole'.
		 * @function
		 * @returns {undefined}
		 * @memberof EditRolePermissions
		 * @version 0.0.6
		 */
		clearForm () {
			this.editingRole = {
				app_permissions: []
			}
		},
		/**
		 * To close the edit role permissions panel by redirecting to the dashboard
		 * @function
		 * @returns {undefined}
		 * @memberof EditRolePermissions
		 * @version 0.0.8
		 */
		closePanel () {
			this.$router.push(this.baseRoute)
		}
	},
	watch: {
		/**
		 * To `watch` the $route in order to determine if this panel should be showing or not
		 * @function
		 * @returns {undefined}
		 * @memberof EditRolePermissions
		 * @version 0.0.8
		 */
		'$route' () {
			if (this.$route.name === this.baseRouteName + ' - Role Permissions') {
				this.activePanel = true
				this.clearError()
				this.clearForm()

				if (this.$route.params.role_id && this.$route.params.role_id !== 'undefined' && parseInt(this.$route.params.role_id) !== 0) {
					this.roleId = this.$route.params.role_id
					this.resetApps()
					this.getBusinessDetails()
				} else {
					this.closePanel()
				}
			} else {
				this.activePanel = false
			}
		}
	}
}
</script>


// WEBPACK FOOTER //
// EditRolePermissions.vue?18a02ea5